import { ColumnDef } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { SortableColumn } from "@/components/shared/sortable-column";
import { formatDateTime } from "@/utils/global";
import {
    CommandTeamFileManagerAction,
    type CommandTeamFile,
    getExtensionFromMime,
    openFileInNewTab,
} from "@/components/command-team/file-manager/tables/command-team-file-manager-actions";

const FILE_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
    PDF:  { bg: "#FEE2E2", text: "#DC2626" },
    JPG:  { bg: "#FEF9C3", text: "#CA8A04" },
    JPEG: { bg: "#FEF9C3", text: "#CA8A04" },
    PNG:  { bg: "#DBEAFE", text: "#2563EB" },
    GIF:  { bg: "#EDE9FE", text: "#7C3AED" },
    SVG:  { bg: "#FCE7F3", text: "#DB2777" },
    WEBP: { bg: "#DBEAFE", text: "#2563EB" },
    DOCX: { bg: "#DBEAFE", text: "#1D4ED8" },
    DOC:  { bg: "#DBEAFE", text: "#1D4ED8" },
    XLSX: { bg: "#D1FAE5", text: "#059669" },
    XLS:  { bg: "#D1FAE5", text: "#059669" },
    CSV:  { bg: "#D1FAE5", text: "#059669" },
    PPTX: { bg: "#FFEDD5", text: "#EA580C" },
    PPT:  { bg: "#FFEDD5", text: "#EA580C" },
    MP4:  { bg: "#EDE9FE", text: "#7C3AED" },
    MP3:  { bg: "#FDF4FF", text: "#A21CAF" },
    ZIP:  { bg: "#F3F4F6", text: "#374151" },
    MD:   { bg: "#F0FDF4", text: "#16A34A" },
    TXT:  { bg: "#F9FAFB", text: "#6B7280" },
};

function FileTypeIcon({ ext }: { ext: string }) {
    const color = FILE_TYPE_COLORS[ext] ?? { bg: "#F3F4F6", text: "#6B7280" };
    return (
        <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: color.bg }}
        >
            <span
                className="font-mono text-[9px] font-bold leading-none tracking-wide"
                style={{ color: color.text }}
            >
                {ext.slice(0, 4)}
            </span>
        </div>
    );
}

function TypeBadge({ type }: { type: string }) {
    return (
        <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-1 font-mono text-[10px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
            .{type}
        </span>
    );
}

function StatusBadge({ status }: { status: string }) {
    return (
        <span className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
            status === "UPLOADED" && "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400",
            status === "PROCESSING" && "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
            status === "FAILED" && "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400",
            status !== "UPLOADED" && status !== "PROCESSING" && status !== "FAILED" && "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
        )}>
            <span className={cn(
                "h-1.5 w-1.5 rounded-full",
                status === "UPLOADED" && "bg-violet-500",
                status === "PROCESSING" && "bg-amber-500",
                status === "FAILED" && "bg-red-500",
                status !== "UPLOADED" && status !== "PROCESSING" && status !== "FAILED" && "bg-zinc-400",
            )} />
            {status.charAt(0) + status.slice(1).toLowerCase()}
        </span>
    );
}

function SourceBadge({ source }: { source: string }) {
    return (
        <span className={cn(
            "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold",
            source === "USER" && "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400",
            source === "AI" && "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
            source === "AGENT" && "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
            source === "SYSTEM" && "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400",
            source !== "USER" && source !== "AI" && source !== "AGENT" && source !== "SYSTEM" && "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
        )}>
            {source.charAt(0) + source.slice(1).toLowerCase()}
        </span>
    );
}

export function getCommandTeamFileManagerColumns({
    formatFileSize,
    handleSort,
    sortBy,
    sortOrder,
}: {
    formatFileSize: (size: number) => string;
    handleSort: (column: string) => void;
    sortBy: string;
    sortOrder: string;
}): ColumnDef<CommandTeamFile>[] {
    return [
        {
            accessorKey: "file_name",
            header: () => (
                <SortableColumn
                    label="Document"
                    columnKey="file_name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const file = row.original;
                const name = (row.getValue("file_name") as string) || "N/A";
                const hasUrl = Boolean(file.file_url);
                const extFromName = file.file_name?.split(".").pop()?.toUpperCase() ?? "";
                const ext =
                    extFromName && extFromName.length <= 6
                        ? extFromName
                        : getExtensionFromMime(file.mime_type).toUpperCase();
                return (
                    <div className="flex items-center gap-3 pl-4">
                        <FileTypeIcon ext={ext || "FILE"} />
                        <div className="flex min-w-0 flex-col">
                            {hasUrl ? (
                                <button
                                    type="button"
                                    onClick={() => openFileInNewTab({ file_url: file.file_url!, mime_type: file.mime_type })}
                                    className="max-w-[200px] truncate text-left text-sm font-medium transition-colors hover:text-primary"
                                >
                                    {name}
                                </button>
                            ) : (
                                <span className="max-w-[200px] truncate text-sm font-medium">{name}</span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: "mime_type",
            header: () => (
                <SortableColumn
                    label="Type"
                    columnKey="mime_type"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const file = row.original;
                const extFromName = file.file_name?.split(".").pop()?.toUpperCase();
                const typeLabel: string =
                    extFromName && extFromName.length <= 6
                        ? extFromName
                        : !file.mime_type
                          ? "N/A"
                          : getExtensionFromMime(file.mime_type).toUpperCase();
                return (
                    <div className="pl-4">
                        {typeLabel === "N/A" ? (
                            <span className="text-sm text-muted-foreground">N/A</span>
                        ) : (
                            <TypeBadge type={typeLabel} />
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: "file_size",
            header: () => (
                <SortableColumn
                    label="Size"
                    columnKey="file_size"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const size = row.getValue("file_size") as number | undefined;
                return (
                    <div className="pl-4 font-mono text-sm tabular-nums text-muted-foreground">
                        {size != null ? formatFileSize(size) : "N/A"}
                    </div>
                );
            },
        },
        {
            accessorKey: "status",
            header: () => (
                <SortableColumn
                    label="Status"
                    columnKey="status"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const status = (row.getValue("status") as string | undefined)?.toUpperCase();
                if (!status) return <div className="pl-4 text-sm text-muted-foreground">N/A</div>;
                return (
                    <div className="pl-4">
                        <StatusBadge status={status} />
                    </div>
                );
            },
        },
        {
            accessorKey: "source",
            header: () => (
                <span className="inline-flex items-center pl-4 text-sm font-medium">
                    Source
                </span>
            ),
            cell: ({ row }) => {
                const source = (row.getValue("source") as string | undefined)?.toUpperCase() || "";
                if (!source) return <div className="pl-4 text-sm text-muted-foreground">N/A</div>;
                return (
                    <div className="pl-4">
                        <SourceBadge source={source} />
                    </div>
                );
            },
        },
        {
            accessorKey: "created_at",
            header: () => (
                <SortableColumn
                    label="Created At"
                    columnKey="created_at"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const date = row.getValue("created_at") as string;
                if (!date) return <div className="pl-4 text-sm text-muted-foreground">N/A</div>;
                const { date: dateStr, time } = formatDateTime(date);
                return (
                    <div className="flex flex-col pl-4">
                        <span className="text-sm">{dateStr}</span>
                        <span className="text-xs text-muted-foreground">{time}</span>
                    </div>
                );
            },
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => <CommandTeamFileManagerAction row={row} />,
        },
    ];
}
